import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { ThemeContext, type ThemePreference } from "./theme-context";

/**
 * Theme switcher (#161, #166). Three values: System / Light / Dark.
 *
 * Persisted in localStorage per device - operators on shared
 * registers all share the device-level setting. Per-user preference
 * can layer on later via the profile if needed.
 *
 * The provider toggles ``<html class="dark">`` based on the resolved
 * theme. The CSS-var token system (``index.css`` + ``tailwind.config.ts``)
 * picks up the class and flips every semantic token (``--canvas``,
 * ``--surface``, ``--text-strong``, etc.) - components consume the
 * tokens via ``bg-canvas`` / ``text-strong`` / ``border-line`` and
 * thus theme automatically without per-component ``dark:`` variants.
 *
 * brand_config stays single-mode light per the project rule; the
 * partner accent is the same in both themes, only the neutral
 * palette flips.
 *
 * Adding a new theme: define a ``.theme-X`` block in ``index.css``
 * with the same token keys, extend ``ThemePreference``, and register
 * it in ``ThemePicker``. No component edits required.
 *
 * Receipt + print stylesheets are unaffected - print media defaults
 * to white background, and the Receipt component hardcodes light
 * surfaces so a paper print is always legible regardless of app
 * theme.
 */

const STORAGE_KEY = "tilt-pos.theme";

function readStored(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return "system";
}

function resolveSystem(): "light" | "dark" {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStored());
  const [systemResolved, setSystemResolved] = useState<"light" | "dark">(() => resolveSystem());

  // Track the system preference live so a user on "System" follows
  // their OS toggle without reloading.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent): void =>
      setSystemResolved(e.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const resolved: "light" | "dark" = preference === "system" ? systemResolved : preference;

  // Toggle the `dark` class on <html> + persist the preference.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", resolved === "dark");
  }, [resolved]);

  const setPreference = (next: ThemePreference): void => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
    setPreferenceState(next);
  };

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}
